// Library methods
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Skeleton from "@mui/material/Skeleton";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import { ChevronLeftIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type BreadcrumbType = {
  isLoading: boolean;
  breadcrumbLink: { href: string; title: string };
  breadcrumbParts: Array<string>;
  breadcrumbState: {
    stateId?: string;
    fromReport?: boolean;
    selectedTab?: number;
  };
  icon?: JSX.Element;
  optionalButton?: JSX.Element;
};

const Breadcrumb = ({
  isLoading,
  breadcrumbLink,
  breadcrumbParts,
  breadcrumbState,
  icon,
  optionalButton,
}: BreadcrumbType) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const formattedBreadcrumbParts = useMemo(() => {
    if (breadcrumbParts.length > 2) {
      return ["...", breadcrumbParts[breadcrumbParts?.length - 1]];
    }
    return breadcrumbParts;
  }, [breadcrumbParts]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isLoading ? (
          <Skeleton
            variant="rounded"
            width="250px"
            sx={{
              height: "24px",
              borderRadius: "4px",
            }}
          />
        ) : (
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#5B6D7A",
            }}
          >
            <Link
              underline="hover"
              color="inherit"
              component={RouterLink}
              to={breadcrumbLink.href}
              state={breadcrumbState}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {icon ?? <ChevronLeftIcon />}
                <span style={{ textDecoration: "underline" }}>
                  {breadcrumbLink.title}
                </span>
                {optionalButton}
              </Box>
            </Link>
            {formattedBreadcrumbParts.map((breadcrumbPart, index) => (
              <Typography key={`${breadcrumbPart}-${index}`} color={onSurface}>
                {breadcrumbPart}
              </Typography>
            ))}
          </Breadcrumbs>
        )}
      </Box>
    </Box>
  );
};

export default Breadcrumb;
